/**
 * PlateChart: renders the ORME Plate Method payload (macro slices + ingredient
 * portions) as a CSS conic-gradient donut with a legend. No chart library.
 *
 * Theme-agnostic: colours are fixed per macro key; the surrounding container
 * sets the background and text colour. Works on both the neutral admin/seller
 * surface and the warm customer portal.
 */
const MACRO_COLORS = {
  protein: '#b15f2e',
  fat: '#d98a4e',
  fiber: '#6f8f6a',
  ash: '#8a7a6c',
  moisture: '#5e7a8c',
  nfe: '#c9a227'
};

function donutBackground(macros) {
  const total = macros.reduce((s, m) => s + (m.percent || 0), 0);
  if (total <= 0) return 'conic-gradient(#e0d8cc 0 100%)';
  let acc = 0;
  const stops = macros.map((m) => {
    const start = (acc / total) * 100;
    acc += m.percent || 0;
    const end = (acc / total) * 100;
    const color = MACRO_COLORS[m.key] || '#b9a995';
    return `${color} ${start}% ${end}%`;
  });
  return `conic-gradient(${stops.join(', ')})`;
}

export function PlateChart({ plate, dense = false }) {
  if (!plate) return null;
  const macros = plate.macros || [];

  return (
    <div className={dense ? 'flex items-center gap-4' : 'flex flex-col items-center gap-5 sm:flex-row sm:items-start'}>
      <div className="relative shrink-0" style={{ width: dense ? 96 : 140, height: dense ? 96 : 140 }}>
        <div className="h-full w-full rounded-full" style={{ background: donutBackground(macros) }} />
        <div className="absolute inset-[18%] flex flex-col items-center justify-center rounded-full bg-[var(--plate-hole,#fff)] text-center">
          {plate.energyKcal != null ? (
            <>
              <span className="text-sm font-semibold leading-none">{plate.energyKcal}</span>
              <span className="text-[10px] opacity-70">kcal/{plate.basisGrams}g</span>
            </>
          ) : (
            <span className="text-[10px] opacity-70">{plate.basisGrams}g</span>
          )}
        </div>
      </div>

      <div className="min-w-0 flex-1">
        {macros.length > 0 ? (
          <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            {macros.map((m) => (
              <li key={m.key} className="flex items-center gap-2">
                <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: MACRO_COLORS[m.key] || '#b9a995' }} />
                <span className="flex-1 truncate">{m.label}</span>
                <span className="tabular-nums opacity-80">{m.percent}%{m.grams != null ? ` · ${m.grams}g` : ''}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm opacity-70">Profilo nutrizionale non ancora disponibile per questo prodotto.</p>
        )}

        {!plate.complete && macros.length > 0 && (
          <p className="mt-2 text-xs opacity-60">Analisi parziale: alcuni valori non sono dichiarati.</p>
        )}

        {Array.isArray(plate.ingredients) && plate.ingredients.length > 0 && !dense && (
          <div className="mt-3">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide opacity-60">Ingredienti</p>
            <div className="flex flex-wrap gap-1.5">
              {plate.ingredients.map((ing, i) => (
                <span key={i} className="rounded-full border border-current/15 px-2 py-0.5 text-xs opacity-90" style={{ borderColor: 'rgba(0,0,0,0.12)' }}>
                  {ing.name}{ing.percent != null ? ` ${ing.percent}%` : ''}{ing.allergen ? ' ⚠' : ''}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
